import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { fetchEGWInsightsForVerse, fetchEGWBookInfo, fetchPreviousParagraph, fetchNextParagraph, fetchEGWFullPageContent } from '../../../services/egwService';
import { cleanEGWText } from '../../../utils/textCleaner';

const EGWInsightsDisplay = ({ currentBook, currentChapter, selectedVerse = null }) => {
  const [insights, setInsights] = useState({ scriptureIndex: [], bibleReferences: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null); // Only one item expanded at a time
  const [contentCache, setContentCache] = useState({}); // Cache fetched content
  const [expandedParagraphs, setExpandedParagraphs] = useState({}); // Track expanded paragraphs
  const [loadingContent, setLoadingContent] = useState({});
  const [showFullPageModal, setShowFullPageModal] = useState(false);
  const [fullPageContent, setFullPageContent] = useState(null);
  const [fullPageLoading, setFullPageLoading] = useState(false);
  const [curatedAccordionOpen, setCuratedAccordionOpen] = useState(false);
  const [directQuotesAccordionOpen, setDirectQuotesAccordionOpen] = useState(false);

  useEffect(() => {
    if (selectedVerse) {
      fetchInsights();
    } else {
      setInsights({ scriptureIndex: [], bibleReferences: [] });
      setLoading(false);
    }
  }, [currentBook, currentChapter, selectedVerse]);

  const fetchInsights = async () => {
    if (!selectedVerse) return;

    try {
      setLoading(true);
      setError(null);
      setExpandedItem(null); // Reset expansion when verse changes
      setContentCache({});
      setExpandedParagraphs({});

      const data = await fetchEGWInsightsForVerse(currentBook, currentChapter, selectedVerse);

      // Fetch book info for all unique book codes
      const allBookCodes = [
        ...new Set([
          ...data?.scriptureIndex?.map(item => item?.egw_book_code),
          ...data?.bibleReferences?.map(item => item?.book_code)
        ])
      ];

      const bookMap = await fetchEGWBookInfo(allBookCodes);

      // Enhance data with book info
      const enhancedScriptureIndex = data?.scriptureIndex?.map(item => ({
        ...item,
        bookInfo: bookMap?.[item?.egw_book_code] || { title: item?.egw_book_code }
      }));

      const enhancedBibleReferences = data?.bibleReferences?.map(item => ({
        ...item,
        bookInfo: bookMap?.[item?.book_code] || { title: item?.book_code }
      }));

      setInsights({
        scriptureIndex: enhancedScriptureIndex,
        bibleReferences: enhancedBibleReferences
      });
    } catch (err) {
      console.error('Error fetching EGW insights:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleItemExpansion = async (itemId, type) => {
    // If clicking the same item, collapse it
    if (expandedItem === itemId) {
      setExpandedItem(null);
      return;
    }

    // Expand new item and collapse previous
    setExpandedItem(itemId);

    // Fetch content immediately if not cached
    if (!contentCache?.[itemId]) {
      await fetchContentForItem(itemId, type);
    }
  };

  const fetchContentForItem = async (itemId, type) => {
    setLoadingContent(prev => ({ ...prev, [itemId]: true }));

    try {
      if (type === 'scriptureIndex') {
        // For scripture index, auto-fetch page content immediately
        const item = insights?.scriptureIndex?.find(ref => 
          `si-${ref?.id}` === itemId
        );

        if (!item) return;

        // Fetch content range using page_start
        const { data: pageContent, error } = await supabase
          ?.from('egw_content')
          ?.select('content_sequence_id, book_code, page_number, paragraph_number, content, toc_title, book_id')
          ?.eq('book_code', item?.egw_book_code)
          ?.eq('page_number', item?.page_start?.toString())
          ?.order('paragraph_number', { ascending: true });

        if (error) throw error;

        if (pageContent && pageContent?.length > 0) {
          setContentCache(prev => ({
            ...prev,
            [itemId]: { 
              type: 'pageContent', 
              content: pageContent,
              bookId: pageContent?.[0]?.book_id,
              pageNumber: pageContent?.[0]?.page_number
            }
          }));
        }
      } else if (type === 'bibleReference') {
        // For bible references, fetch the specific paragraph using book_code, page, and paragraph
        const item = insights?.bibleReferences?.find(ref => 
          `br-${ref?.book_code}-${ref?.page}-${ref?.paragraph}` === itemId
        );
        
        if (item?.book_code && item?.page && item?.paragraph) {
          // Query using book_code, page_number, and paragraph_number
          const { data: content, error } = await supabase
            ?.from('egw_content')
            ?.select('content_sequence_id, book_code, book_id, page_number, paragraph_number, content, toc_title')
            ?.eq('book_code', item?.book_code)
            ?.eq('page_number', item?.page?.toString())
            ?.eq('paragraph_number', item?.paragraph?.toString())
            ?.maybeSingle();

          if (error) throw error;

          if (content) {
            setContentCache(prev => ({
              ...prev,
              [itemId]: { 
                type: 'paragraph', 
                content: [content],
                bookId: content?.book_id,
                pageNumber: content?.page_number
              }
            }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      setContentCache(prev => ({
        ...prev,
        [itemId]: { type: 'error', error: err?.message }
      }));
    } finally {
      setLoadingContent(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleExpandParagraph = async (itemId, direction) => {
    const cached = contentCache?.[itemId];
    if (!cached?.content) return;

    const key = `${itemId}-${direction}`;
    setLoadingContent(prev => ({ ...prev, [key]: true }));

    try {
      const currentContent = cached?.content;
      let newParagraph;

      if (direction === 'before') {
        const firstSequenceId = currentContent?.[0]?.content_sequence_id;
        newParagraph = await fetchPreviousParagraph(firstSequenceId);
        
        if (newParagraph) {
          setContentCache(prev => ({
            ...prev,
            [itemId]: {
              ...cached,
              content: [newParagraph, ...currentContent]
            }
          }));
        }
      } else if (direction === 'after') {
        const lastSequenceId = currentContent?.[currentContent?.length - 1]?.content_sequence_id;
        newParagraph = await fetchNextParagraph(lastSequenceId);
        
        if (newParagraph) {
          setContentCache(prev => ({
            ...prev,
            [itemId]: {
              ...cached,
              content: [...currentContent, newParagraph]
            }
          }));
        }
      }
    } catch (err) {
      console.error('Error expanding paragraph:', err);
    } finally {
      setLoadingContent(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleViewFullPage = async (bookId, pageNumber) => {
    setFullPageLoading(true);
    setShowFullPageModal(true);

    try {
      const content = await fetchEGWFullPageContent(bookId, pageNumber);
      setFullPageContent({
        content,
        bookId,
        pageNumber,
        bookCode: content?.[0]?.book_code,
        tocTitle: content?.[0]?.toc_title
      });
    } catch (err) {
      console.error('Error fetching full page:', err);
      setFullPageContent({ error: err?.message });
    } finally {
      setFullPageLoading(false);
    }
  };

  const closeFullPageModal = () => {
    setShowFullPageModal(false);
    setFullPageContent(null);
  };

  // Format reference as {BookCode pg.paragraph}
  const formatReference = (bookCode, page, paragraph) => {
    return `{${bookCode} ${page}.${paragraph}}`;
  };

  const formatHeaderReference = (bookInfo, page) => {
    const title = bookInfo?.title || 'Unknown';
    return `${title} pg ${page}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Icon name="Loader" className="w-6 h-6 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">Loading EGW Insights...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-4">
        <div className="flex items-start">
          <Icon name="AlertCircle" className="w-5 h-5 text-red-600 mt-0.5 mr-2" />
          <div>
            <h3 className="text-sm font-semibold text-red-800">Error Loading Insights</h3>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedVerse) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center m-4">
        <Icon name="BookOpen" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Select a Verse</h3>
        <p className="text-sm text-gray-600">
          Click on a verse in {currentBook} {currentChapter} to see Ellen White references.
        </p>
      </div>
    );
  }

  const hasScriptureIndex = insights?.scriptureIndex?.length > 0;
  const hasBibleReferences = insights?.bibleReferences?.length > 0;

  if (!hasScriptureIndex && !hasBibleReferences) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center m-4">
        <Icon name="BookOpen" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No EGW Insights Available</h3>
        <p className="text-sm text-gray-600">
          No Ellen White references found for {currentBook} {currentChapter}:{selectedVerse}.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 space-y-3">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start">
            <Icon name="Info" className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <h3 className="text-xs font-semibold text-blue-800">EGW Insights</h3>
              <p className="text-xs text-blue-700 mt-1">
                References for {currentBook} {currentChapter}:{selectedVerse}. Click to expand.
              </p>
            </div>
          </div>
        </div>

        {/* Curated References Section with Category Accordion */}
        {hasScriptureIndex && (
          <div className="space-y-1">
            {/* Category Header - Accordion Toggle */}
            <button
              onClick={() => setCuratedAccordionOpen(!curatedAccordionOpen)}
              className="w-full flex items-center justify-between p-2 bg-blue-100 hover:bg-blue-150 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 bg-blue-500 rounded"></div>
                <h3 className="text-sm font-semibold text-gray-800">Curated References</h3>
                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded font-medium">
                  {insights?.scriptureIndex?.length}
                </span>
              </div>
              <Icon 
                name={curatedAccordionOpen ? 'ChevronUp' : 'ChevronDown'} 
                className="w-4 h-4 text-blue-600" 
              />
            </button>
            
            {/* Category Content */}
            {curatedAccordionOpen && (
              <div className="space-y-1 pl-1">
                {insights?.scriptureIndex?.map((item) => {
                  const itemId = `si-${item?.id}`;
                  const isExpanded = expandedItem === itemId;
                  const cached = contentCache?.[itemId];
                  const isLoading = loadingContent?.[itemId];

                  return (
                    <div
                      key={itemId}
                      className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden transition-all"
                    >
                      {/* Header - Always Visible */}
                      <button
                        onClick={() => toggleItemExpansion(itemId, 'scriptureIndex')}
                        className="w-full p-2 flex items-center justify-between hover:bg-blue-100 transition-colors text-left"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-blue-900">
                              {formatHeaderReference(item?.bookInfo, item?.page_start)}
                            </span>
                          </div>
                        </div>
                        <Icon 
                          name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                          className="w-4 h-4 text-blue-600" 
                        />
                      </button>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="px-2 pb-2 border-t border-blue-200">
                          {isLoading ? (
                            <div className="flex items-center justify-center py-3">
                              <Icon name="Loader" className="w-4 h-4 animate-spin text-blue-600" />
                              <span className="ml-2 text-xs text-blue-700">Loading content...</span>
                            </div>
                          ) : cached?.type === 'pageContent' && cached?.content ? (
                            <div className="space-y-2 mt-2">
                              {cached?.content?.map((para, idx) => (
                                <div key={para?.content_sequence_id} className="bg-white p-2 rounded border border-blue-200">
                                  <p className="text-xs text-gray-700 leading-relaxed">{cleanEGWText(para?.content)}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {formatReference(para?.book_code, para?.page_number, para?.paragraph_number)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : cached?.type === 'error' ? (
                            <p className="text-xs text-red-600 py-2">Error: {cached?.error}</p>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Direct Quotes Section with Category Accordion */}
        {hasBibleReferences && (
          <div className="space-y-1">
            {/* Category Header - Accordion Toggle */}
            <button
              onClick={() => setDirectQuotesAccordionOpen(!directQuotesAccordionOpen)}
              className="w-full flex items-center justify-between p-2 bg-green-100 hover:bg-green-150 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 bg-green-500 rounded"></div>
                <h3 className="text-sm font-semibold text-gray-800">Direct Quotes</h3>
                <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded font-medium">
                  {insights?.bibleReferences?.length}
                </span>
              </div>
              <Icon 
                name={directQuotesAccordionOpen ? 'ChevronUp' : 'ChevronDown'} 
                className="w-4 h-4 text-green-600" 
              />
            </button>
            
            {/* Category Content */}
            {directQuotesAccordionOpen && (
              <div className="space-y-1 pl-1">
                {insights?.bibleReferences?.map((item) => {
                  const itemId = `br-${item?.book_code}-${item?.page}-${item?.paragraph}`;
                  const isExpanded = expandedItem === itemId;
                  const cached = contentCache?.[itemId];
                  const isLoading = loadingContent?.[itemId];

                  return (
                    <div
                      key={itemId}
                      className="bg-green-50 border border-green-200 rounded-lg overflow-hidden transition-all"
                    >
                      {/* Header - Always Visible */}
                      <button
                        onClick={() => toggleItemExpansion(itemId, 'bibleReference')}
                        className="w-full p-2 flex items-center justify-between hover:bg-green-100 transition-colors text-left"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-green-900">
                              {formatHeaderReference(item?.bookInfo, item?.page)}
                            </span>
                          </div>
                        </div>
                        <Icon 
                          name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                          className="w-4 h-4 text-green-600" 
                        />
                      </button>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="px-2 pb-2 border-t border-green-200">
                          {isLoading ? (
                            <div className="flex items-center justify-center py-3">
                              <Icon name="Loader" className="w-4 h-4 animate-spin text-green-600" />
                              <span className="ml-2 text-xs text-green-700">Loading paragraph...</span>
                            </div>
                          ) : cached?.type === 'paragraph' && cached?.content ? (
                            <div className="space-y-2 mt-2">
                              {/* Expand Before Button */}
                              <Button
                                onClick={() => handleExpandParagraph(itemId, 'before')}
                                variant="ghost"
                                size="sm"
                                className="w-full text-green-700 hover:bg-green-100 h-7 text-xs"
                                disabled={loadingContent?.[`${itemId}-before`]}
                              >
                                {loadingContent?.[`${itemId}-before`] ? (
                                  <Icon name="Loader" className="w-3 h-3 animate-spin mr-1" />
                                ) : (
                                  <Icon name="ChevronUp" className="w-3 h-3 mr-1" />
                                )}
                                Load Previous
                              </Button>

                              {/* Paragraphs */}
                              {cached?.content?.map((para, idx) => (
                                <div key={para?.content_sequence_id} className="bg-white p-2 rounded border border-green-200">
                                  <p className="text-xs text-gray-700 leading-relaxed">{cleanEGWText(para?.content)}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {formatReference(para?.book_code, para?.page_number, para?.paragraph_number)}
                                  </p>
                                </div>
                              ))}

                              {/* Expand After Button */}
                              <Button
                                onClick={() => handleExpandParagraph(itemId, 'after')}
                                variant="ghost"
                                size="sm"
                                className="w-full text-green-700 hover:bg-green-100 h-7 text-xs"
                                disabled={loadingContent?.[`${itemId}-after`]}
                              >
                                {loadingContent?.[`${itemId}-after`] ? (
                                  <Icon name="Loader" className="w-3 h-3 animate-spin mr-1" />
                                ) : (
                                  <Icon name="ChevronDown" className="w-3 h-3 mr-1" />
                                )}
                                Load Next
                              </Button>

                              {/* View Full Page Button */}
                              {cached?.bookId && cached?.pageNumber && (
                                <Button
                                  onClick={() => handleViewFullPage(cached?.bookId, cached?.pageNumber)}
                                  variant="outline"
                                  size="sm"
                                  className="w-full mt-1 h-7 text-xs"
                                >
                                  <Icon name="Maximize2" className="w-3 h-3 mr-1" />
                                  View Full Page
                                </Button>
                              )}
                            </div>
                          ) : cached?.type === 'error' ? (
                            <p className="text-xs text-red-600 py-2">Error: {cached?.error}</p>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Full Page Modal */}
      {showFullPageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {fullPageContent?.bookCode} - Page {fullPageContent?.pageNumber}
                </h2>
                {fullPageContent?.tocTitle && (
                  <p className="text-sm text-gray-600 mt-1">{fullPageContent?.tocTitle}</p>
                )}
              </div>
              <button
                onClick={closeFullPageModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Icon name="X" className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {fullPageLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader" className="w-8 h-8 animate-spin text-primary" />
                  <span className="ml-3 text-gray-600">Loading full page...</span>
                </div>
              ) : fullPageContent?.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600">Error: {fullPageContent?.error}</p>
                </div>
              ) : fullPageContent?.content ? (
                <div className="space-y-4">
                  {fullPageContent?.content?.map((para) => (
                    <div key={para?.content_sequence_id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-800 leading-relaxed">{cleanEGWText(para?.content)}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {formatReference(para?.book_code, para?.page_number, para?.paragraph_number)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t flex justify-end">
              <Button onClick={closeFullPageModal} variant="outline">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EGWInsightsDisplay;